<script setup>
import { computed, ref } from 'vue'
import { timeToMinutes } from '../../lib/date'

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, required: true },
  error: { type: String, default: '' },
  hint: { type: String, default: '' },
  stepMinutes: { type: Number, default: 15 },
  showNow: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false }
})

const emit = defineEmits(['update:modelValue'])
const input = ref(null)

const helpId = computed(() => `time-help-${props.label.toLowerCase().replace(/\s+/g, '-')}`)

function normalizeMinutes(value) {
  const total = ((Number(value) % 1440) + 1440) % 1440
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`
}

function adjust(delta) {
  const current = timeToMinutes(props.modelValue)
  emit('update:modelValue', normalizeMinutes((current ?? 0) + delta))
}

function setNow() {
  const now = new Date()
  const rounded = Math.round((now.getHours() * 60 + now.getMinutes()) / props.stepMinutes) * props.stepMinutes
  emit('update:modelValue', normalizeMinutes(rounded))
}

function openPicker() {
  if (props.disabled) return
  if (typeof input.value?.showPicker === 'function') input.value.showPicker()
  else input.value?.focus()
}
</script>

<template>
  <div>
    <span class="input-label">{{ label }}</span>

    <div class="mobile-time-field sm:hidden" :class="error ? '!border-danger-200 !ring-4 !ring-danger-50' : ''">
      <input
        ref="input"
        :value="modelValue"
        type="time"
        :step="stepMinutes * 60"
        :aria-label="label"
        :disabled="disabled"
        :aria-invalid="Boolean(error)"
        :aria-describedby="error || hint ? helpId : undefined"
        @input="emit('update:modelValue', $event.target.value)"
        @click="openPicker"
      />
    </div>

    <input
      :value="modelValue"
      type="time"
      :step="stepMinutes * 60"
      :disabled="disabled"
      class="input-field hidden sm:block"
      :class="error ? '!border-danger-200 !ring-danger-50' : ''"
      :aria-invalid="Boolean(error)"
      :aria-describedby="error || hint ? helpId : undefined"
      @input="emit('update:modelValue', $event.target.value)"
    />

    <div class="mt-2 flex gap-2 sm:hidden" aria-label="Быстрая корректировка времени">
      <button type="button" class="mobile-time-adjust" :disabled="disabled" @click="adjust(-stepMinutes)">−{{ stepMinutes }} мин</button>
      <button v-if="showNow" type="button" class="mobile-time-adjust" :disabled="disabled" @click="setNow">Сейчас</button>
      <button type="button" class="mobile-time-adjust" :disabled="disabled" @click="adjust(stepMinutes)">+{{ stepMinutes }} мин</button>
    </div>

    <span v-if="error || hint" :id="helpId" class="mt-1.5 block text-xs font-semibold" :class="error ? 'text-danger-700' : 'text-app-muted'">
      {{ error || hint }}
    </span>
  </div>
</template>
